import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import DirectTokenPurchaseModal from './DirectTokenPurchaseModal';
import styles from '../../styles/Dialog.module.css';
import TrialService from '../../services/auth/TrialService';
import AuthService from '../../services/auth/AuthService';
import { getModalThemeStyles } from '../../utils/themeStyles';
import { createCountdownTimer, formatTimeUntil } from '../../utils/timeUtils';

const FreeTrialLimitModal = ({ isOpen, onClose, initialStatus }) => {
  const { isDarkMode } = useTheme();
  const { login, error: authError } = useAuth();
  const [status, setStatus] = useState(initialStatus || null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [token, setToken] = useState('');
  const [timeRemaining, setTimeRemaining] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is the random test route
  const isRandomTestRoute = location.pathname === '/test-aleatorio';

  const handleTokenSubmit = async () => {
    if (!token.trim()) return;
    
    try {
      // Use AuthService to validate token
      await AuthService.login(token.trim());
      // On successful login, close modal
      onClose();
    } catch (error) {
      console.error('Error validating token:', error);
      alert(error.message || 'Error al validar el token. Por favor, intenta de nuevo.');
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const initializeStatus = async () => {
      // If we have initialStatus, use it and don't make API calls
      if (initialStatus) {
        setStatus(initialStatus);
      } else {
        // Only make one API call when the modal is first opened
        const currentStatus = await TrialService.getUserStatus(true);
        setStatus(currentStatus);
      }
    };

    initializeStatus();

    // Set up countdown timer if there's a nextTestTime
    let timer;
    if (status?.nextTestTime) {
      timer = createCountdownTimer(status.nextTestTime, setTimeRemaining);
      timer.start();
    } else {
      setTimeRemaining('No disponible');
    }

    return () => {
      // Clean up timer on unmount
      if (timer) {
        timer.stop();
      }
    };
  }, [isOpen, initialStatus, status?.nextTestTime]);

  if (!isOpen) return null;

  const handleNavigateHome = () => {
    onClose();
    navigate('/');
  };

  const handleShowPurchaseModal = () => {
    onClose(); // Close the trial limit modal first
    navigate('/'); // Go back to home where the purchase modal will be shown
  };

  // Get theme styles
  const cssVariables = getModalThemeStyles(isDarkMode);

  // Get message based on trial status
  const getStatusMessage = () => {
    if (!status) return 'Prueba gratuita no disponible.';
    
    if (status.ipLimited) {
      return 'Esta dirección IP ya ha utilizado su prueba gratuita diaria.';
    }
    
    if (status.sessionLimited) {
      return 'Ya has realizado tu test gratuito diario. Vuelve mañana para realizar otro test.';
    }
    
    if (status.testsAvailable === 0) {
      return 'Has alcanzado el límite diario de tests gratuitos.';
    }
    
    return 'Prueba gratuita disponible.';
  };

  return (
    <>
     <div className={styles.overlay} onClick={handleNavigateHome} style={cssVariables}>
        <div
          className={styles.dialog}
          onClick={e => e.stopPropagation()}
          style={{
            maxHeight: '85vh',
            overflowY: 'auto',
            padding: 'clamp(0.4rem, 1.5vw, 0.6rem)',
            fontSize: '0.95rem'
          }}
        >
          <h2 className={styles.title}>
            {isRandomTestRoute ? 'LÍMITE DE PRUEBA GRATUITA' : 'CONTENIDO PREMIUM'}
          </h2>

          <div className={styles.section} style={{ textAlign: 'center', marginBottom: 'clamp(0.3rem, 1.5vw, 0.7rem)' }}>
            {isRandomTestRoute ? (
              <>
                <p style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', margin: 0 }}>
                  {getStatusMessage()}
                </p>
                <p style={{ fontSize: 'clamp(0.8rem, 2vw, 1rem)', margin: 'clamp(0.25rem, 1vw, 0.5rem) 0 0 0' }}>
                  Recuerda que puedes realizar 1 test aleatorio gratuito al día.
                </p>
              </>
            ) : (
              <p style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', margin: 0 }}>
                Esta sección requiere acceso premium.
              </p>
            )}
          </div>

          {isRandomTestRoute && status?.nextTestTime && (
            <div className={styles.section} style={{ textAlign: 'center', marginBottom: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
              <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', fontWeight: 'bold', margin: 0 }}>
                Próximo test disponible en: {timeRemaining || 'Calculando...'}
              </p>
            </div>
          )}

          <div className={styles.section}>
            <h3 style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>
              ¿YA TIENES UN TOKEN?
            </h3>

            <input
              type="text"
              placeholder="Introduce tu token de acceso"
              className={styles.input}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTokenSubmit()}
            />

            <button
              onClick={handleTokenSubmit}
              className={styles.actionButton}
              style={{
                width: '100%',
                maxWidth: '300px',
                backgroundColor: '#00B8D4',
                color: '#fff',
                border: `3px solid ${isDarkMode ? '#fff' : '#000'}`,
                borderRadius: '4px',
                padding: '10px 15px',
                margin: '0.3rem auto',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                display: 'block',
                boxShadow: `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`,
                transform: 'translate(-2px, -2px)',
                transition: 'transform 0.1s, box-shadow 0.1s',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translate(-4px, -4px)';
                e.currentTarget.style.boxShadow = `6px 6px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
            >
              Validar token
            </button>
          </div>

          <div className={styles.section}>
            <h3 style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>
              ¿NO TIENES TOKEN?
            </h3>

            <div className={styles.price} style={{ margin: '0.3rem 0' }}>
              <span>Desde 1,49€</span>
              <span className={styles.highlight}>SEMANA</span>
            </div>

            <button
              onClick={handleShowPurchaseModal}
              className={styles.actionButton}
              style={{
                width: '100%',
                maxWidth: '300px',
                backgroundColor: '#4CAF50',
                color: '#fff',
                border: `3px solid ${isDarkMode ? '#fff' : '#000'}`,
                borderRadius: '4px',
                padding: '10px 15px',
                margin: '0.3rem auto',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                display: 'block',
                boxShadow: `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`,
                transform: 'translate(-2px, -2px)',
                transition: 'transform 0.1s, box-shadow 0.1s',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translate(-4px, -4px)';
                e.currentTarget.style.boxShadow = `6px 6px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
            >
              Comprar acceso premium →
            </button>

            <ul className={styles.featureList} style={{ margin: '0.3rem auto 0', gap: '0.1rem', fontSize: '0.9rem' }}>
              <li>✓ Tests ilimitados</li>
              <li>✓ Corrección instantánea</li>
              <li>✓ Explicaciones detalladas</li>
              <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                ✓ Garantía de aprobado
              </li>
            </ul>
          </div>

          <div style={{ margin: '0.7rem auto 0.5rem', width: '100%', maxWidth: '350px' }}>
            <button
              onClick={handleNavigateHome}
              className={styles.secondaryButton}
              style={{
                width: '100%',
                backgroundColor: 'transparent',
                color: isDarkMode ? '#fff' : '#000',
                border: `3px solid ${isDarkMode ? '#fff' : '#000'}`,
                borderRadius: '4px',
                padding: '12px 20px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                display: 'block',
                boxShadow: `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`,
                transform: 'translate(-2px, -2px)',
                transition: 'transform 0.1s, box-shadow 0.1s',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translate(-4px, -4px)';
                e.currentTarget.style.boxShadow = `6px 6px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
                e.currentTarget.style.boxShadow = `4px 4px 0 ${isDarkMode ? '#fff' : '#000'}`;
              }}
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FreeTrialLimitModal;